// Client configuration and constants

import type { SupportedLanguage } from '../utils/content/languageUtils';

/**
 * One UI string per supported language.
 *
 * Every i18n map below is pinned to this type, so forgetting (or misspelling) a
 * language code is a compile error instead of a silent English fallback at
 * runtime. IX-3322 is the failure this prevents: `pt` was in
 * `SUPPORTED_LANGUAGES` for months while every widget string stayed English,
 * because the maps inferred their keys from whatever happened to be written.
 *
 * Type-only import — erased at compile time, so it does not create a runtime
 * cycle with `languageUtils` (which reaches back into this module's config).
 */
type LocalizedText = Record<SupportedLanguage, string>;

/**
 * Pick the caller's language out of an i18n map, falling back to English.
 *
 * `language` is a plain string everywhere it enters the widget (URL segment,
 * browser locale, site config), so the cast is unavoidable — this is the one
 * place that does it.
 */
const resolveLocalized = (text: LocalizedText, language: string): string =>
    text[language as SupportedLanguage] || text.en;

const DEFAULT_FRONTEND_VERSION = '0.0.0-dummy';

const getInjectedFrontendVersion = (): string => {
    if (typeof __INBOUNDX_VERSION__ !== 'undefined' && __INBOUNDX_VERSION__) {
        return __INBOUNDX_VERSION__;
    }

    return DEFAULT_FRONTEND_VERSION;
};

// API Version - injected by app tooling when version.json exists.
export const FRONTEND_VERSION = getInjectedFrontendVersion();

/**
 * Get Git commit SHA from build-time injected global variable
 * Returns 'unknown' if not available (e.g., in development without build)
 */
export function getGitCommitSha(): string {
    const win = window as unknown as Record<string, unknown>;
    if (typeof window !== 'undefined' && win.__INBOUNDX_GIT_COMMIT_SHA__) {
        return win.__INBOUNDX_GIT_COMMIT_SHA__ as string;
    }
    // For widget/playground/chrome-plugin (uses Vite DefinePlugin)
    if (typeof __INBOUNDX_GIT_COMMIT_SHA__ !== 'undefined') {
        return __INBOUNDX_GIT_COMMIT_SHA__;
    }
    return 'unknown';
}
// Common API configuration shared by all sites
// Uses Vite environment variables (replaced at build time by Vite)
export const COMMON_API_CONFIG = {
    rose_api_url: import.meta.env.VITE_IX_API_URL || '', // Global fallback when site-specific api_endpoint is null
    rose_api_key: '', // API keys must be supplied by the host integration, never bundled in shared package
};

// SUPPORTED_SITES has been moved to Supabase database
// Use SiteConfigManager functions to access site configurations

// Chat UI constants
export const CHAT_CONFIG = {
    // Dimensions
    DIMENSIONS: {
        COLLAPSED: {
            WIDTH: '800px',
            HEIGHT: '72px',
            BORDER_RADIUS: '20px',
            OUTER_PADDING: '12px',
            INNER_PADDING: '12px',
            // Mobile responsive dimensions (for screens < 1236px)
            MOBILE_WIDTH: '95vw',
            MOBILE_MAX_WIDTH: '600px',
            MOBILE_MARGIN: '16px',
        },
        EXPANDED: {
            WIDTH: '1202px',
            HEIGHT: '594px',
            BORDER_RADIUS: '16px 16px 0px 0px',
            INPUT_WIDTH: '812px',
            // Mobile responsive dimensions (for screens < 1236px)
            MOBILE_WIDTH: '95vw',
            MOBILE_HEIGHT: '80vh',
            MOBILE_MAX_HEIGHT: '500px',
            MOBILE_INPUT_WIDTH: '90%',
            MOBILE_MARGIN: '16px',
        },
    },

    // Responsive breakpoints
    BREAKPOINTS: {
        MOBILE: '768px',
        TABLET: '1024px',
        DESKTOP: '1236px', // Matches existing MOBILE_DETECTION breakpoint
    },

    // Colors
    COLORS: {
        BACKGROUND: {
            WHITE: '#FFFFFF',
            LIGHT_GRAY: '#FAFAFA',
            CHAT_BG: '#F9FBFF',
        },
        BORDERS: {
            LIGHT: '#E8E8E8',
            MEDIUM: '#D9D9D9',
        },
        TEXT: {
            PRIMARY: '#505050',
            SECONDARY: '#656565',
            DARK: '#050906',
            BLUE: '#0A42C3',
        },
    },
};

// API Configuration
export const API_CONFIG = {
    // API modes
    API_MODES: {
        NEW: 'new',
        LEGACY: 'legacy',
    },

    // Default endpoints (legacy endpoints only - new API uses COMMON_API_CONFIG)
    DEFAULT_ENDPOINTS: {
        LEGACY_API: 'https://inboundx.app.n8n.cloud/webhook/beb0b98d-2b24-4f46-ad40-51417073b215',
        EVALUATION: 'https://inboundx.app.n8n.cloud/webhook/63ac3224-3ecc-4196-856f-ee41de30858c',
    },
};

// Default configuration - should be overridden by environment-specific configs
export const DEFAULT_CONFIG = {
    API_URL: undefined,
    SITE_NAME: undefined,
    DOMAIN: undefined,
};

// Suggested questions
export const SUGGESTED_QUESTIONS = ['What are the main features?', 'Compare with competitors'];

// SearchBar placeholder text configuration with language support
// {{company_name}} will be replaced with the actual company name
export const SEARCH_BAR_PLACEHOLDERS = {
    COLLAPSED: {
        en: 'Ask anything about {{company_name}}',
        fr: 'Posez-nous vos questions sur {{company_name}}',
        es: 'Pregúntanos sobre {{company_name}}',
        it: 'Chiedici di {{company_name}}',
        de: 'Frag uns zu {{company_name}}',
        pt: 'Pergunte qualquer coisa sobre {{company_name}}',
        bg: 'Попитайте всичко за {{company_name}}',
        nl: 'Vraag alles over {{company_name}}',
        ro: 'Întrebați orice despre {{company_name}}',
        hu: 'Kérdezzen bármit a(z) {{company_name}} vállalatról',
    },
    COLLAPSED_COPILOT: {
        en: 'How can we help you?',
        fr: 'Comment pouvons-nous vous aider ?',
        es: '¿Cómo podemos ayudarle?',
        it: 'Come possiamo aiutarti?',
        de: 'Wie können wir Ihnen helfen?',
        pt: 'Como podemos ajudar?',
        bg: 'Как можем да помогнем?',
        nl: 'Hoe kunnen we helpen?',
        ro: 'Cum vă putem ajuta?',
        hu: 'Hogyan segíthetünk?',
    },
    EXPANDED_NEW_CONVERSATION: {
        en: '',
        fr: '',
        de: '',
        es: '',
        it: '',
        pt: '',
        bg: '',
        nl: '',
        ro: '',
        hu: '',
    },
    POST_CONVERSION: {
        en: 'Type your answer...',
        fr: 'Tapez votre réponse...',
        de: 'Geben Sie Ihre Antwort ein...',
        es: 'Escriba su respuesta...',
        it: 'Digita la tua risposta...',
        pt: 'Digite sua resposta...',
        bg: 'Въведете своя отговор...',
        nl: 'Typ je antwoord...',
        ro: 'Introduceți răspunsul...',
        hu: 'Írja be a válaszát...',
    },
    EXPANDED_EXISTING_CONVERSATION: {
        en: 'What else can I help you with?',
        fr: "Avez-vous d'autres questions ?",
        de: 'Womit kann ich Ihnen noch helfen?',
        es: '¿En qué más puedo ayudarte?',
        it: "In cos'altro posso aiutarti?",
        pt: 'Em que mais posso ajudar?',
        bg: 'С какво друго мога да ви помогна?',
        nl: 'Waarmee kan ik je nog meer helpen?',
        ro: 'Cu ce altceva vă mai pot ajuta?',
        hu: 'Miben segíthetek még?',
    },
} as const satisfies Record<string, LocalizedText>;

// Booking field prompt pill — persistent reminder shown while the booking flow
// is waiting for a required field (email or any extra field configured by the
// client, e.g. team_size). Distinct from the legal disclaimer copy.
//
// {{field}} is replaced with the localized label for the current field id from
// BOOKING_FIELD_LABELS. Unknown ids fall back to a humanized version of the id
// (e.g. "team_size" → "team size").
const BOOKING_FIELD_PROMPT_TEMPLATE = {
    en: 'Enter your {{field}} to continue',
    fr: 'Entrez votre {{field}} pour continuer',
    de: 'Geben Sie Ihre {{field}} ein, um fortzufahren',
    es: 'Introduce tu {{field}} para continuar',
    it: 'Inserisci il tuo {{field}} per continuare',
    pt: 'Digite seu {{field}} para continuar',
    bg: 'Въведете своя {{field}}, за да продължите',
    nl: 'Vul je {{field}} in om door te gaan',
    ro: 'Introduceți {{field}} pentru a continua',
    hu: 'A folytatáshoz adja meg a(z) {{field}} értékét',
} as const satisfies LocalizedText;

const BOOKING_FIELD_LABELS: Record<string, LocalizedText> = {
    email: {
        en: 'email',
        fr: 'email',
        de: 'E-Mail',
        es: 'email',
        it: 'email',
        pt: 'email',
        bg: 'имейл',
        nl: 'e-mailadres',
        ro: 'e-mail',
        hu: 'e-mail-cím',
    },
    team_size: {
        en: 'team size',
        fr: "taille d'équipe",
        de: 'Teamgröße',
        es: 'tamaño del equipo',
        it: 'dimensione del team',
        pt: 'tamanho da equipe',
        bg: 'размер на екипа',
        nl: 'teamgrootte',
        ro: 'dimensiunea echipei',
        hu: 'csapat mérete',
    },
    company_size: {
        en: 'company size',
        fr: "taille d'entreprise",
        de: 'Unternehmensgröße',
        es: 'tamaño de la empresa',
        it: 'dimensione azienda',
        pt: 'tamanho da empresa',
        bg: 'размер на компанията',
        nl: 'bedrijfsgrootte',
        ro: 'dimensiunea companiei',
        hu: 'vállalat mérete',
    },
    role: {
        en: 'role',
        fr: 'rôle',
        de: 'Rolle',
        es: 'rol',
        it: 'ruolo',
        pt: 'função',
        bg: 'роля',
        nl: 'functie',
        ro: 'rol',
        hu: 'beosztás',
    },
    industry: {
        en: 'industry',
        fr: 'secteur',
        de: 'Branche',
        es: 'industria',
        it: 'settore',
        pt: 'setor',
        bg: 'отрасъл',
        nl: 'branche',
        ro: 'industrie',
        hu: 'iparág',
    },
};

const humanizeFieldId = (fieldId: string): string => fieldId.replace(/_/g, ' ');

const getBookingFieldLabel = (fieldId: string, language: string): string => {
    const entry = BOOKING_FIELD_LABELS[fieldId];
    if (entry) {
        return resolveLocalized(entry, language) || humanizeFieldId(fieldId);
    }
    return humanizeFieldId(fieldId);
};

export const getBookingFieldPromptText = (
    fieldId: string | null,
    language: string
): string | null => {
    if (!fieldId) {
        return null;
    }
    const template = resolveLocalized(BOOKING_FIELD_PROMPT_TEMPLATE, language);
    return template.replace('{{field}}', getBookingFieldLabel(fieldId, language));
};

// Shown in the booking top bar during the initial backend round trip, before
// the reducer has received the first pendingFieldId. Without this fallback the
// top bar would render an empty center.
const BOOKING_PREPARING_TEXT = {
    en: 'One moment…',
    fr: 'Un instant…',
    de: 'Einen Moment…',
    es: 'Un momento…',
    it: 'Un momento…',
    pt: 'Um momento…',
    bg: 'Само момент…',
    nl: 'Een moment…',
    ro: 'Un moment…',
    hu: 'Egy pillanat…',
} as const satisfies LocalizedText;

export const getBookingPreparingText = (language: string): string =>
    resolveLocalized(BOOKING_PREPARING_TEXT, language);

// MinimizedChatView button text with language support
export const MINIMIZED_CHAT_VIEW_TEXT = {
    ASK_BUTTON: {
        en: 'Ask a question',
        fr: 'Poser une question',
        de: 'Eine Frage stellen',
        es: 'Hacer una pregunta',
        it: 'Fai una domanda',
        pt: 'Fazer uma pergunta',
        bg: 'Задайте въпрос',
        nl: 'Stel een vraag',
        ro: 'Puneți o întrebare',
        hu: 'Tegyen fel egy kérdést',
    },
    ASK_BUTTON_COPILOT: {
        en: 'Need help?',
        fr: "Besoin d'aide ?",
        de: 'Brauchen Sie Hilfe?',
        es: '¿Necesita ayuda?',
        it: 'Hai bisogno di aiuto?',
        pt: 'Precisa de ajuda?',
        bg: 'Нуждаете се от помощ?',
        nl: 'Hulp nodig?',
        ro: 'Aveți nevoie de ajutor?',
        hu: 'Segítségre van szüksége?',
    },
} as const satisfies Record<string, LocalizedText>;

// Helper function to get placeholder text for the search bar based on language
export const getSearchBarPlaceholder = (
    type:
        | 'COLLAPSED'
        | 'COLLAPSED_COPILOT'
        | 'EXPANDED_NEW_CONVERSATION'
        | 'POST_CONVERSION'
        | 'EXPANDED_EXISTING_CONVERSATION',
    language: string,
    companyName?: string,
    override?: Partial<Record<string, string>>
): string => {
    let placeholder: string =
        override?.[language] ||
        override?.en ||
        resolveLocalized(SEARCH_BAR_PLACEHOLDERS[type], language);

    // Replace {{company_name}} with actual company name if provided
    if (companyName && placeholder.includes('{{company_name}}')) {
        placeholder = placeholder.replace('{{company_name}}', companyName);
    }

    return placeholder;
};

// Helper function to get MinimizedChatView text based on language
export const getMinimizedChatViewText = (
    type: keyof typeof MINIMIZED_CHAT_VIEW_TEXT,
    language: string
): string => resolveLocalized(MINIMIZED_CHAT_VIEW_TEXT[type], language);

// ThinkingAnimation text with language support
export const THINKING_ANIMATION_TEXT = {
    PLANNING: {
        en: 'Planning next moves',
        fr: 'Planification des prochaines étapes',
        de: 'Nächste Schritte planen',
        es: 'Planificando próximos pasos',
        it: 'Pianificazione dei prossimi passi',
        pt: 'Planejando próximos passos',
        bg: 'Планиране на следващите стъпки',
        nl: 'Volgende stappen plannen',
        ro: 'Planificarea pașilor următori',
        hu: 'Következő lépések tervezése',
    },
    PROCESSING: {
        en: 'Processing context',
        fr: 'Traitement du contexte',
        de: 'Kontext verarbeiten',
        es: 'Procesando contexto',
        it: 'Elaborazione del contesto',
        pt: 'Processando contexto',
        bg: 'Обработване на контекста',
        nl: 'Context verwerken',
        ro: 'Procesarea contextului',
        hu: 'Kontextus feldolgozása',
    },
    CRAFTING: {
        en: 'Crafting response',
        fr: 'Élaboration de la réponse',
        de: 'Antwort erstellen',
        es: 'Elaborando respuesta',
        it: 'Creazione della risposta',
        pt: 'Elaborando resposta',
        bg: 'Създаване на отговор',
        nl: 'Antwoord opstellen',
        ro: 'Redactarea răspunsului',
        hu: 'Válasz megfogalmazása',
    },
    GENERATING: {
        en: 'Generating',
        fr: 'Génération',
        de: 'Generierung',
        es: 'Generando',
        it: 'Generazione',
        pt: 'Gerando',
        bg: 'Генериране',
        nl: 'Genereren',
        ro: 'Generare',
        hu: 'Létrehozás',
    },
} as const satisfies Record<string, LocalizedText>;

// Helper function to get ThinkingAnimation text based on language
export const getThinkingAnimationText = (
    type: keyof typeof THINKING_ANIMATION_TEXT,
    language: string
): string => resolveLocalized(THINKING_ANIMATION_TEXT[type], language);

// Follow-up questions label text with language support
export const FOLLOW_UP_QUESTIONS_TEXT = {
    LABEL: {
        en: 'Related questions',
        fr: 'Questions connexes',
        de: 'Verwandte Fragen',
        es: 'Preguntas relacionadas',
        it: 'Domande correlate',
        pt: 'Perguntas relacionadas',
        bg: 'Свързани въпроси',
        nl: 'Gerelateerde vragen',
        ro: 'Întrebări similare',
        hu: 'Kapcsolódó kérdések',
    },
    // Label for the initial form assistant welcome message's follow-up questions
    FORM_ASSISTANT_WELCOME_LABEL: {
        en: 'Frequently asked questions',
        fr: 'Questions fréquentes',
        de: 'Häufig gestellte Fragen',
        es: 'Preguntas frecuentes',
        it: 'Domande frequenti',
        pt: 'Perguntas frequentes',
        bg: 'Често задавани въпроси',
        nl: 'Veelgestelde vragen',
        ro: 'Întrebări frecvente',
        hu: 'Gyakran ismételt kérdések',
    },
} as const satisfies Record<string, LocalizedText>;

// Helper function to get follow-up questions text based on language
export const getFollowUpQuestionsText = (
    type: keyof typeof FOLLOW_UP_QUESTIONS_TEXT,
    language: string
): string => resolveLocalized(FOLLOW_UP_QUESTIONS_TEXT[type], language);

// IX-4266: "start a new conversation" control in the expanded chat header.
// Labelled by outcome ("New conversation"), not by implementation ("Reset").
export const NEW_CONVERSATION_TEXT = {
    LABEL: {
        en: 'New conversation',
        fr: 'Nouvelle conversation',
        de: 'Neue Unterhaltung',
        es: 'Nueva conversación',
        it: 'Nuova conversazione',
        pt: 'Nova conversa',
        bg: 'Нов разговор',
        nl: 'Nieuw gesprek',
        ro: 'Conversație nouă',
        hu: 'Új beszélgetés',
    },
    MENU: {
        en: 'Conversation options',
        fr: 'Options de conversation',
        de: 'Unterhaltungsoptionen',
        es: 'Opciones de conversación',
        it: 'Opzioni conversazione',
        pt: 'Opções da conversa',
        bg: 'Опции за разговора',
        nl: 'Gespreksopties',
        ro: 'Opțiuni conversație',
        hu: 'Beszélgetés beállításai',
    },
    COPY_TRANSCRIPT: {
        en: 'Copy transcript',
        fr: 'Copier la conversation',
        de: 'Verlauf kopieren',
        es: 'Copiar conversación',
        it: 'Copia conversazione',
        pt: 'Copiar conversa',
        bg: 'Копирай разговора',
        nl: 'Gesprek kopiëren',
        ro: 'Copiază conversația',
        hu: 'Beszélgetés másolása',
    },
    COPIED: {
        en: 'Copied',
        fr: 'Copié',
        de: 'Kopiert',
        es: 'Copiado',
        it: 'Copiato',
        pt: 'Copiado',
        bg: 'Копирано',
        nl: 'Gekopieerd',
        ro: 'Copiat',
        hu: 'Másolva',
    },
} as const satisfies Record<string, LocalizedText>;

// Helper function to get new-conversation control text based on language
export const getNewConversationText = (
    type: keyof typeof NEW_CONVERSATION_TEXT,
    language: string
): string => resolveLocalized(NEW_CONVERSATION_TEXT[type], language);

// Suggested answers label text with language support
export const SUGGESTED_ANSWERS_TEXT = {
    LABEL: {
        en: 'Suggested answers',
        fr: 'Réponses suggérées',
        de: 'Vorgeschlagene Antworten',
        es: 'Respuestas sugeridas',
        it: 'Risposte suggerite',
        pt: 'Respostas sugeridas',
        bg: 'Предложени отговори',
        nl: 'Voorgestelde antwoorden',
        ro: 'Răspunsuri sugerate',
        hu: 'Javasolt válaszok',
    },
} as const satisfies Record<string, LocalizedText>;

// Helper function to get suggested answers text based on language
export const getSuggestedAnswersText = (
    type: keyof typeof SUGGESTED_ANSWERS_TEXT,
    language: string
): string => resolveLocalized(SUGGESTED_ANSWERS_TEXT[type], language);

// Mobile Detection Configuration
export const MOBILE_DETECTION = {
    // Screen width breakpoint below which widgets should be hidden
    SCREEN_WIDTH_BREAKPOINT: 1236, // px
} as const;

// Widget form factor - controls positioning and layout
export type FormFactor = 'default' | 'copilot';
export type ExpandedLayout = 'expanded' | 'copilot';

// Utility function to check semantic version compatibility
// MAJOR.MINOR.PATCH format
// Compatibility policy: Only MAJOR must match. MINOR and PATCH differences are allowed.
// Examples: 1.0.1 vs 1.1.0 = compatible, 1.0.1 vs 2.0.0 = incompatible
export const isVersionCompatible = (frontendVersion: string, backendVersion: string): boolean => {
    // Parse semantic versions (major.minor.patch)
    const parseFrontendVersion = frontendVersion.split('.').map(Number);
    const parseBackendVersion = backendVersion.split('.').map(Number);

    // Compare only MAJOR component for compatibility
    const frontendMajor = parseFrontendVersion[0];
    const backendMajor = parseBackendVersion[0];

    return frontendMajor === backendMajor;
};

// Helper functions for global config (not site-specific)
export const getDefaultApiUrl = (): string => {
    return COMMON_API_CONFIG.rose_api_url;
};

export const getDefaultApiKey = (): string => {
    return COMMON_API_CONFIG.rose_api_key;
};

// Helper to get effective API URL for a site (site-specific api_endpoint or global fallback)
export const getEffectiveApiUrl = (siteConfig?: { api_endpoint?: string }): string => {
    return siteConfig?.api_endpoint || getDefaultApiUrl();
};

// Hard cap for a single user-typed chat message.
// Layered with a higher defensive check in the API client (MAX_QUERY_CHARS) and a final
// server-side check in QueryRequest; guards against pasted dumps that would otherwise
// blow past the LLM context window downstream.
export const MAX_USER_MESSAGE_CHARS = 10_000;

// Higher defensive cap applied in the API client for programmatic callers that
// bypass the SearchBar input (public API, chrome-plugin, etc.).
export const MAX_QUERY_CHARS = 20_000;

// Form assistant welcome message marker - used to identify the synthetic welcome message
// This marker is used as the "question" content and is hidden in the UI
export const FORM_ASSISTANT_WELCOME_MARKER = '__FORM_ASSISTANT_WELCOME__';

// Post-conversion welcome message marker - used to identify the synthetic welcome message
// on thank-you pages after a visitor has already converted
export const POST_CONVERSION_WELCOME_MARKER = '__POST_CONVERSION_WELCOME__';

// Booking completed marker - sent to backend after a visitor completes a booking
// via the in-chat calendar embed, so the booking handler can respond with a follow-up
export const BOOKING_COMPLETED_MARKER = '__BOOKING_COMPLETED__';

// Resume greeting marker - used as the "question" content for a resumed MCP
// conversation whose first turn is the brand's greeting (the synthetic connect
// opener was dropped server-side, IX-3852). Hidden in the UI so no empty user
// bubble renders above the greeting.
export const RESUME_GREETING_MARKER = '__RESUME_GREETING__';

/**
 * Synthetic turns that drive the chat protocol but are never shown to the
 * visitor. Anything rendering or serializing a transcript must skip them.
 */
export const isHiddenMarker = (content: string | undefined): boolean =>
    content === FORM_ASSISTANT_WELCOME_MARKER ||
    content === POST_CONVERSION_WELCOME_MARKER ||
    content === BOOKING_COMPLETED_MARKER ||
    content === RESUME_GREETING_MARKER;

// Form assistant initial welcome message with language support
const FORM_ASSISTANT_INITIAL_MESSAGE: LocalizedText = {
    en: 'How can I help you?',
    fr: 'Comment puis-je vous aider ?',
    es: '¿Cómo puedo ayudarle?',
    it: 'Come posso aiutarti?',
    de: 'Wie kann ich Ihnen helfen?',
    pt: 'Como posso ajudar?',
    bg: 'Как мога да помогна?',
    nl: 'Hoe kan ik helpen?',
    ro: 'Cum vă pot ajuta?',
    hu: 'Hogyan segíthetek?',
};

// Helper function to get the form assistant initial message based on language
export const getFormAssistantInitialMessage = (language: string): string =>
    resolveLocalized(FORM_ASSISTANT_INITIAL_MESSAGE, language);
